import { addMonthsClamped, emiDueDateYmd, formatLocalYmd, parseLoanStartDate } from './loanScheduleDates';

/**
 * Schedule start for a migrated EMI loan. The schedule's first slot is start + 1 month, so the
 * overdue EMIs sit on the months just before the next due date and keep their real due dates.
 */
export function migratedScheduleStart(nextDueYmd: string, overdueEmis = 0): string {
  const due = parseLoanStartDate(nextDueYmd);
  if (!due) return nextDueYmd;
  const back = Math.max(0, Math.floor(Number(overdueEmis) || 0)) + 1;
  return formatLocalYmd(addMonthsClamped(due, -back));
}

const dayMs = (ymd: string) => Date.parse(`${ymd}T00:00:00Z`);

export function daysBetweenYmd(fromYmd: string, toYmd: string): number {
  const a = dayMs(fromYmd);
  const b = dayMs(toYmd);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86400000));
}

/** Same formula as the overdue engine: (days × EMI) / 365 per unpaid EMI. */
export function overduePenalty(emi: number, days: number): number {
  if (days <= 0 || emi <= 0) return 0;
  return Math.round(((days * emi) / 365) * 100) / 100;
}

export type MigratedOverdueRow = { dueDate: string; days: number; penalty: number };

export type MigratedSchedulePreview = {
  scheduleStart: string;
  firstDue: string;
  nextUpcomingDue: string | null;
  lastDue: string;
  overdue: MigratedOverdueRow[];
  overdueAmount: number;
  oldestDays: number;
  penaltyTotal: number;
};

/** What the software will treat as overdue at `asOfYmd` for a migrated EMI loan. */
export function previewMigratedSchedule(input: {
  nextDueYmd: string;
  overdueEmis: number;
  remainingEmis: number;
  emi: number;
  asOfYmd: string;
}): MigratedSchedulePreview | null {
  const remaining = Math.floor(Number(input.remainingEmis) || 0);
  if (!input.nextDueYmd || !parseLoanStartDate(input.nextDueYmd) || remaining <= 0) return null;
  const emi = Math.max(0, Math.round(Number(input.emi) || 0));
  const scheduleStart = migratedScheduleStart(input.nextDueYmd, input.overdueEmis);
  const overdue: MigratedOverdueRow[] = [];
  let nextUpcomingDue: string | null = null;
  for (let i = 0; i < remaining; i++) {
    const due = emiDueDateYmd(scheduleStart, i);
    if (input.asOfYmd && due < input.asOfYmd) {
      const days = daysBetweenYmd(due, input.asOfYmd);
      overdue.push({ dueDate: due, days, penalty: overduePenalty(emi, days) });
    } else if (!nextUpcomingDue) {
      nextUpcomingDue = due;
    }
  }
  return {
    scheduleStart,
    firstDue: emiDueDateYmd(scheduleStart, 0),
    nextUpcomingDue,
    lastDue: emiDueDateYmd(scheduleStart, remaining - 1),
    overdue,
    overdueAmount: overdue.length * emi,
    oldestDays: overdue[0]?.days ?? 0,
    penaltyTotal: Math.round(overdue.reduce((s, r) => s + r.penalty, 0) * 100) / 100,
  };
}
